import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/shared/PageHeader';
import { LoadingState } from '../../components/shared/LoadingState';
import { ErrorState } from '../../components/shared/ErrorState';
import { DocumentPreviewDialog } from './components/DocumentPreviewDialog';
import { SignatureRequestDialog } from './components/SignatureRequestDialog';
import { SignatureCanvas } from './components/SignatureCanvas';
import { ErrorDialog } from '../../components/shared/ErrorDialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/Dialog';
import { useAuth } from '../../core/contexts/AuthContext';
import { useDocuments } from '../../core/hooks/useDocuments';
import { useClient } from '../../core/hooks/useClient';
import { useLinkedClientAccount } from '../../core/hooks/useLinkedClientAccount';
import { resolveProjectContactEmail } from '../../core/utils/projectContact';
import type { Document, DocumentSignature } from '../../core/models/Document';
import { DocumentStatus, DocumentSignatureStatus } from '../../core/models/Document';
import { collectionGroup, query, where, getDocs, updateDoc, getDoc, Timestamp, DocumentReference } from 'firebase/firestore';
import { db } from '../../core/services/firebase';
import { FileText } from 'lucide-react';
import { ConfirmDialog } from '../../components/shared/ConfirmDialog';
import { logDebug, logError } from '../../lib/utils';

export const DocumentDetailPage: React.FC = () => {
  const { documentId } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  const { currentUser, organizationId } = useAuth();
  const [document, setDocument] = useState<Document | null>(null);
  const [documentRef, setDocumentRef] = useState<DocumentReference | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [signatureRequestOpen, setSignatureRequestOpen] = useState(false);
  const [signatureCanvasOpen, setSignatureCanvasOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(true);
  const [copyingLink, setCopyingLink] = useState(false);
  const [errorDialog, setErrorDialog] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  const { sendSigningLink, submitOrganizationDocumentSignature, deleteDocument } = useDocuments(null, organizationId);
  const { client } = useClient(document?.clientId || null);
  const linkedClientId = client?.clientId || client?.organizationId || null;
  const { clientAccount } = useLinkedClientAccount(linkedClientId);
  const contactEmail = resolveProjectContactEmail(client, clientAccount);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [signing, setSigning] = useState(false);
  const [sharingLink, setSharingLink] = useState(false);

  useEffect(() => {
    const fetchDocument = async () => {
      if (!documentId) {
        setError(new Error('Document ID is required'));
        setLoading(false);
        return;
      }

      try {
        // Documents are stored in subcollections: /clients/{clientId}/documents/{documentId}
        // Use collectionGroup to query across all documents subcollections
        // Since collectionGroup doesn't support querying by document ID directly,
        // we filter by organizationId (if available) to narrow results, then find by ID

        let q;
        if (organizationId && currentUser?.role !== 'SUPER_ADMIN') {
          // Filter by organization for non-super-admin users
          q = query(
            collectionGroup(db, 'documents'),
            where('organizationId', '==', organizationId)
          );
        } else {
          // Super admin or no organizationId - query all documents
          q = query(collectionGroup(db, 'documents'));
        }

        const querySnapshot = await getDocs(q);

        // Find the document with matching ID
        const foundDoc = querySnapshot.docs.find(doc => doc.id === documentId);

        if (foundDoc) {
          const data = foundDoc.data();
          setDocumentRef(foundDoc.ref);
          setDocument({
            id: foundDoc.id,
            ...data,
            createdAt: data.createdAt?.toDate() || data.metadata?.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || data.metadata?.updatedAt?.toDate() || new Date(),
          } as Document);
        } else {
          // Document not found in subcollections
          setError(new Error('Document not found'));
        }
      } catch (err) {
        logError('documents', err, { source: 'DocumentDetailPage.fetchDocument', documentId });
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    fetchDocument();
  }, [documentId, organizationId, currentUser?.role]);

  const handleRequestSignatures = async (signatures: DocumentSignature[]) => {
    if (!document || !documentRef) return;

    try {
      await updateDoc(documentRef, {
        signatures,
        status: DocumentStatus.PENDING_SIGNATURES,
        'metadata.updatedAt': Timestamp.now(),
        'metadata.lastModifiedBy': currentUser?.uid || '',
      });

      setDocument({
        ...document,
        signatures,
        status: DocumentStatus.PENDING_SIGNATURES,
      });
      setSignatureRequestOpen(false);
    } catch (err) {
      logError('documents', err, { source: 'DocumentDetailPage.handleRequestSignatures', documentId: document.id });
      alert('Failed to request signatures. Please try again.');
    }
  };

  const handleSign = async (signatureData?: string) => {
    logDebug('documents', '[handleSign] start', {
      hasSignatureData: Boolean(signatureData),
      hasDocument: Boolean(document),
      hasDocumentRef: Boolean(documentRef),
      hasCurrentUser: Boolean(currentUser),
    });

    if (!document || !documentRef || !currentUser) {
      return;
    }

    setSigning(true);
    try {
      // Determine signature type and data
      const signatureType: 'draw' | 'type' = signatureData ? 'draw' : 'type';
      const typedName = signatureData ? undefined : (currentUser.displayName || currentUser.email || 'Client User');

      logDebug('documents', '[handleSign] submitting', {
        signatureType,
        hasSignatureData: Boolean(signatureData),
        hasTypedName: Boolean(typedName),
      });

      const result = await submitOrganizationDocumentSignature(
        document.id,
        signatureType,
        signatureData, // signature image data URL if draw type
        typedName, // typed name if type signature
        new Date().toISOString()
      );

      // Refresh document to get updated signatures and signed PDF URL
      const docSnap = await getDoc(documentRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        const updatedDoc = {
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate?.() || data.metadata?.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || data.metadata?.updatedAt?.toDate?.() || new Date(),
        } as Document;
        setDocument(updatedDoc);
      }

      setSignatureCanvasOpen(false);

      if (result.signedPdfUrl) {
        logDebug('documents', 'Signed PDF generated', { signedPdfUrl: result.signedPdfUrl });
      }
    } catch (err: any) {
      logError('documents', err, { source: 'DocumentDetailPage.handleSign', documentId: document?.id });
      setErrorDialog({ open: true, message: err.message || 'Failed to sign document. Please try again.' });
    } finally {
      setSigning(false);
    }
  };

  const handleSendSigningLink = async () => {
    if (!document || !contactEmail) {
      setErrorDialog({ open: true, message: 'Client email is required to generate a signing link. Add an email on the linked client account.' });
      return;
    }

    setCopyingLink(true);
    try {
      // Generate signing link (don't send email)
      const result = await sendSigningLink(document.id, contactEmail, false);

      // Copy to clipboard
      await navigator.clipboard.writeText(result.signingLink);

      // Show success notification
      alert('Signing link copied to clipboard!');
    } catch (err: any) {
      logError('documents', err, { source: 'DocumentDetailPage.handleSendSigningLink', documentId: document.id });
      setErrorDialog({
        open: true,
        message: err.message || 'Failed to generate signing link. Please try again.'
      });
    } finally {
      setCopyingLink(false);
    }
  };

  const handleShareLink = async () => {
    if (!document || !contactEmail) {
      setErrorDialog({ open: true, message: 'Client email is required to generate a sharing link. Add an email on the linked client account.' });
      return;
    }

    setSharingLink(true);
    try {
      // Use the signing link for sharing - it will show download option if no signatures required
      // and show sign button if signatures are required
      const result = await sendSigningLink(document.id, contactEmail, false);
      
      if (result.signingLink) {
        await navigator.clipboard.writeText(result.signingLink);
        alert('Document link copied to clipboard! This link allows viewing and downloading the document. If signatures are required, the sign button will appear.');
      } else {
        setErrorDialog({
          open: true,
          message: 'Failed to generate document link. Please try again.'
        });
      }
    } catch (err: any) {
      logError('documents', err, { source: 'DocumentDetailPage.handleShareLink', documentId: document.id });
      setErrorDialog({
        open: true,
        message: err.message || 'Failed to copy link. Please try again.'
      });
    } finally {
      setSharingLink(false);
    }
  };

  const handleDelete = async () => {
    if (!document) return;
    try {
      setDeleting(true);
      await deleteDocument(document.id, document.clientId);
      setDeleteDialogOpen(false);
      navigate('/documents');
    } catch (err) {
      logError('documents', err, { source: 'DocumentDetailPage.handleDelete', documentId: document.id });
      alert('Failed to delete document. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <LoadingState message="Loading document..." />;
  }

  if (error || !document) {
    return <ErrorState message={error?.message || 'Document not found'} />;
  }

  // Check if current user has a pending signature OR if there's a pending organization signature they can sign
  const pendingSignature = document.signatures.find(
    (sig) =>
      sig.userId === currentUser?.uid && sig.status === DocumentSignatureStatus.PENDING
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title={document.title}
        description={`Document details and signature workflow`}
        icon={FileText}
      />

      <DocumentPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        document={document}
        onRequestSignatures={() => setSignatureRequestOpen(true)}
        onSign={() => setSignatureCanvasOpen(true)}
        onBackToDocuments={() => navigate('/documents')}
        onCopySigningLink={handleSendSigningLink}
        onShareLink={handleShareLink}
        onDelete={() => setDeleteDialogOpen(true)}
        copyingLink={copyingLink}
        sharingLink={sharingLink}
        client={client}
      />

      <SignatureRequestDialog
        open={signatureRequestOpen}
        onClose={() => setSignatureRequestOpen(false)}
        onSubmit={handleRequestSignatures}
        documentTitle={document.title}
      />

      <Dialog open={signatureCanvasOpen} onOpenChange={(open) => !signing && setSignatureCanvasOpen(open)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Sign Document</DialogTitle>
          </DialogHeader>
          {signing ? (
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="text-sm text-gray-600">Signing document and generating PDF...</p>
            </div>
          ) : (
            <SignatureCanvas
              onSave={handleSign}
              onCancel={() => setSignatureCanvasOpen(false)}
              userName={currentUser?.displayName || currentUser?.email || undefined}
              userTitle={pendingSignature?.title}
            />
          )}
        </DialogContent>
      </Dialog>

      <ErrorDialog
        open={errorDialog.open}
        onClose={() => setErrorDialog({ open: false, message: '' })}
        message={errorDialog.message}
      />
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          if (deleting) return;
          setDeleteDialogOpen(false);
        }}
        onConfirm={handleDelete}
        title="Delete document"
        description="Are you sure you want to permanently delete this document? This will also remove any related PDFs and signature files."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="destructive"
        isLoading={deleting}
      />
    </div>
  );
};
